import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';

// Brand palette, forest green (slightly lighter than #1A3C2B as requested)
const BRAND = {
  forest:      '#22503A',   // slightly lighter forest green (primary)
  forestDark:  '#1A3C2B',   // deep forest (hover / accents)
  forestLight: '#2E6B4F',   // lighter for gradient end
  sage:        '#8AA395',   // muted sage, secondary text / icons
  cream:       '#F4F1EA',   // warm off-white background
  surface:     '#FFFFFF',
  textPrimary: '#16241C',
  textMuted:   '#5B6B62',
};

// System prompt, defines the AI's persona and knowledge about WebIdeal
const SYSTEM_PROMPT = `You are Mahi, a friendly and professional AI support assistant for WebIdeal, an AI-Smart Hiring platform that offers ATS (Applicant Tracking System), CRM, HRMS, and Job Portal tools for employers and recruiters.

Your personality:
- Warm, professional, and concise, like a real human support agent
- You use natural conversational language, not robotic or overly formal
- You keep replies short and focused (2,4 sentences max unless the user asks for detail)
- You use light punctuation and occasional line breaks for readability, never long walls of text

Your knowledge about WebIdeal:
- Products: ATS, CRM, HRMS, Job Portal, all unified in one platform
- Target users: Employers, HR teams, and recruiters
- Key features: AI-powered candidate screening, interview scheduling, pipeline management, smart matching
- Contact: Email Info@webidealglobal.com | Phone +44 7359 973201
- Plans: Starter, Professional, Enterprise
- Website: webidealglobal.com

Guidelines:
- If someone asks about pricing, direct them to contact the sales team
- If someone has a technical issue, empathise and offer to connect them with support
- If you don't know something specific, be honest and offer to connect them with the team
- Never make up specific prices, contract terms, or data
- Always end ambiguous conversations by offering a next step (demo, email, call)
- Do NOT use markdown like **bold** or bullet lists with hyphens, write in plain conversational prose`;

const LiveChat = () => {
  // Chat window stays CLOSED until the user clicks the FAB, no more auto-open
  const [isOpen, setIsOpen] = useState(false);

  // Controls the floating greeting bubble's fade in / fade out lifecycle
  // 'hidden' -> 'entering' -> 'visible' -> 'leaving' -> 'hidden'
  const [bubbleState, setBubbleState] = useState('hidden');

  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // conversationHistory tracks the full message thread for the API (role/content pairs)
  const [conversationHistory, setConversationHistory] = useState([]);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const greetingShown = useRef(false);
  const bubbleTimers = useRef([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // ── Floating greeting bubble: fades in shortly after page load, stays
  // for a few seconds, then fades back out, all without opening the chat.
  useEffect(() => {
    // Let the page settle for a moment before greeting the visitor
    const showTimer = setTimeout(() => setBubbleState('entering'), 1200);
    const settleTimer = setTimeout(() => setBubbleState('visible'), 1200 + 450);
    const hideTimer = setTimeout(() => setBubbleState('leaving'), 1200 + 450 + 7000);
    const removeTimer = setTimeout(() => setBubbleState('hidden'), 1200 + 450 + 7000 + 450);

    bubbleTimers.current = [showTimer, settleTimer, hideTimer, removeTimer];
    return () => bubbleTimers.current.forEach(clearTimeout);
  }, []);

  const clearBubbleTimers = () => {
    bubbleTimers.current.forEach(clearTimeout);
  };

  const dismissBubble = () => {
    clearBubbleTimers();
    setBubbleState('leaving');
    setTimeout(() => setBubbleState('hidden'), 450);
  };

  const openChatFromBubble = () => {
    dismissBubble();
    setIsOpen(true);
  };

  // Show professional time-based greeting on first open
  useEffect(() => {
    if (!isOpen || greetingShown.current) return;
    greetingShown.current = true;

    const hour = new Date().getHours();
    const timeGreeting =
      hour < 12 ? 'Good morning' :
      hour < 17 ? 'Good afternoon' :
      hour < 21 ? 'Good evening' : 'Good evening';

    const greetings = [
      `${timeGreeting}! 👋 Welcome to WebIdeal.`,
      "I'm Mahi, your AI assistant here. How can I help you today?",
    ];

    greetings.forEach((text, i) => {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + i,
          type: 'bot',
          text,
          timestamp: new Date(),
        }]);
      }, i * 800);
    });
  }, [isOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const userText = message.trim();
    const userMsg = {
      id: Date.now(),
      type: 'user',
      text: userText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setMessage('');
    setIsTyping(true);

    // Build updated history including this new user message
    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: userText },
    ];
    setConversationHistory(updatedHistory);

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: updatedHistory,
        }),
      });

      const data = await response.json();
      const botText = data?.content?.[0]?.text || "I'm sorry, I couldn't process that. Please try again or email Info@webidealglobal.com for help.";

      // Save assistant reply into history
      setConversationHistory(prev => [...prev, { role: 'assistant', content: botText }]);

      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: botText,
        timestamp: new Date(),
      }]);
    } catch {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: "Sorry, I'm having trouble connecting right now. Please email Info@webidealglobal.com or call +44 7359 973201 for immediate help.",
        timestamp: new Date(),
      }]);
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const bubbleActive = bubbleState !== 'hidden' && !isOpen;

  return (
    <>
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: scale(0.88) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes typingDot {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40%            { transform: translateY(-4px); opacity: 1; }
        }
        .chat-input:focus { outline: none; box-shadow: 0 0 0 2px ${BRAND.forest}44; }
        .chat-send-btn:hover:not(:disabled) { background: ${BRAND.forestDark} !important; }
        .chat-send-btn:disabled { opacity: 0.45; cursor: not-allowed; }

        .greeting-bubble {
          opacity: 0;
          transform-origin: right center;
          transform: translateX(28px) scale(0.94);
          transition: opacity 0.55s cubic-bezier(0.4, 0, 0.2, 1),
                      transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          will-change: opacity, transform;
        }
        .greeting-bubble.is-visible {
          opacity: 1;
          transform: translateX(0) scale(1);
          pointer-events: auto;
        }
        .greeting-bubble.is-leaving {
          opacity: 0;
          transform: translateX(28px) scale(0.95);
          transition: opacity 0.5s cubic-bezier(0.4, 0, 1, 1),
                      transform 0.5s cubic-bezier(0.4, 0, 1, 1);
          pointer-events: none;
        }
      `}</style>

      {/* ── Floating greeting bubble + FAB button, shown only when chat is closed ── */}
      {!isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-end',
            gap: '10px',
          }}
        >
          {bubbleActive && (
            <div
              className={`greeting-bubble ${bubbleState === 'visible' ? 'is-visible' : ''} ${bubbleState === 'leaving' ? 'is-leaving' : ''}`}
              style={{
                position: 'relative',
                maxWidth: '235px',
                background: `linear-gradient(135deg, ${BRAND.forestDark} 0%, ${BRAND.forest} 70%, ${BRAND.forestLight} 100%)`,
                borderRadius: '16px 16px 4px 16px',
                padding: '13px 15px',
                boxShadow: `0 10px 30px ${BRAND.forestDark}55, 0 2px 8px rgba(0,0,0,0.1)`,
                cursor: 'pointer',
              }}
              onClick={openChatFromBubble}
            >
              <button
                onClick={(e) => { e.stopPropagation(); dismissBubble(); }}
                aria-label="Dismiss greeting"
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: BRAND.surface,
                  border: `1px solid ${BRAND.forestDark}22`,
                  color: BRAND.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                }}
              >
                <X style={{ width: '11px', height: '11px' }} />
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6ee7a0', display: 'inline-block' }} />
                <span style={{
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.75)',
                  fontFamily: 'monospace',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}>
                  Mahi
                </span>
              </div>
              <p style={{
                margin: 0,
                fontSize: '13px',
                lineHeight: '1.45',
                color: '#fff',
                fontWeight: 500,
              }}>
                Hello! I'm Mahi, your AI assistant here. How can I help you today?
              </p>

              {/* speech-bubble tail, points sideways at the message icon,
                  so it reads as the icon "speaking" this message */}
              <div style={{
                position: 'absolute',
                right: '-7px',
                bottom: '18px',
                width: '16px',
                height: '16px',
                background: BRAND.forestLight,
                transform: 'rotate(45deg)',
                borderRadius: '0 4px 0 0',
                boxShadow: `3px -3px 8px ${BRAND.forestDark}40`,
              }} />
            </div>
          )}

          <button
            onClick={() => { dismissBubble(); setIsOpen(true); }}
            aria-label="Open chat"
            style={{
              width: '58px',
              height: '58px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${BRAND.forest}, ${BRAND.forestLight})`,
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 32px ${BRAND.forestDark}55`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'scale(1.08)';
              e.currentTarget.style.boxShadow = `0 12px 40px ${BRAND.forestDark}77`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = `0 8px 32px ${BRAND.forestDark}55`;
            }}
          >
            <MessageSquare style={{ width: '24px', height: '24px' }} />
          </button>
        </div>
      )}

      {/* ── Chat window ── */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            width: '380px',
            height: '520px',
            maxHeight: 'calc(100vh - 48px)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: `0 24px 64px ${BRAND.forestDark}44, 0 4px 16px ${BRAND.forestDark}22`,
            border: `1px solid ${BRAND.forestDark}22`,
            display: 'flex',
            flexDirection: 'column',
            animation: 'chatSlideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)',
            transformOrigin: 'bottom right',
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              background: `linear-gradient(135deg, ${BRAND.forestDark} 0%, ${BRAND.forest} 60%, ${BRAND.forestLight} 100%)`,
              padding: '16px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* subtle grid texture */}
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.04, pointerEvents: 'none',
              backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)',
              backgroundSize: '24px 24px',
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
              {/* Avatar */}
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                border: '1.5px solid rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <MessageSquare style={{ width: '18px', height: '18px', color: '#fff' }} />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '15px', color: '#fff', letterSpacing: '-0.01em' }}>
                  Mahi
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6ee7a0', display: 'inline-block' }} />
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)', fontFamily: 'monospace', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Online
                  </span>
                </div>
              </div>
            </div>

            {/* Header actions */}
            <div style={{ display: 'flex', gap: '4px', position: 'relative' }}>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
                style={{
                  background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer',
                  width: '28px', height: '28px', borderRadius: '6px', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                <X style={{ width: '14px', height: '14px' }} />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <>
            {/* Messages */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  padding: '16px',
                  background: BRAND.cream,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div style={{ maxWidth: '82%' }}>
                      <div
                        style={{
                          padding: '10px 14px',
                          borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: msg.type === 'user'
                            ? `linear-gradient(135deg, ${BRAND.forestDark}, ${BRAND.forest})`
                            : BRAND.surface,
                          color: msg.type === 'user' ? '#fff' : BRAND.textPrimary,
                          fontSize: '13.5px',
                          lineHeight: '1.5',
                          boxShadow: msg.type === 'user'
                            ? `0 2px 8px ${BRAND.forestDark}33`
                            : '0 1px 4px rgba(0,0,0,0.07)',
                          border: msg.type === 'bot' ? `1px solid ${BRAND.forestDark}12` : 'none',
                        }}
                      >
                        {msg.text}
                      </div>
                      <p style={{
                        margin: '3px 4px 0',
                        fontSize: '10px',
                        color: BRAND.textMuted,
                        textAlign: msg.type === 'user' ? 'right' : 'left',
                        fontFamily: 'monospace',
                        letterSpacing: '0.02em',
                      }}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      padding: '10px 16px',
                      borderRadius: '16px 16px 16px 4px',
                      background: BRAND.surface,
                      border: `1px solid ${BRAND.forestDark}12`,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                    }}>
                      {[0, 1, 2].map(i => (
                        <span key={i} style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          background: BRAND.sage,
                          display: 'inline-block',
                          animation: `typingDot 1.2s ease-in-out infinite`,
                          animationDelay: `${i * 0.2}s`,
                        }} />
                      ))}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* ── Input ── */}
              <form
                onSubmit={handleSendMessage}
                style={{
                  padding: '12px 14px',
                  borderTop: `1px solid ${BRAND.forestDark}15`,
                  background: BRAND.surface,
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message…"
                  className="chat-input"
                  style={{
                    flex: 1,
                    height: '38px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: `1.5px solid ${BRAND.forestDark}20`,
                    background: BRAND.cream,
                    fontSize: '13px',
                    color: BRAND.textPrimary,
                    transition: 'box-shadow 0.15s, border-color 0.15s',
                  }}
                  onFocus={e => {
                    e.currentTarget.style.borderColor = BRAND.forest;
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = `${BRAND.forestDark}20`;
                  }}
                />
                <button
                  type="submit"
                  disabled={!message.trim()}
                  className="chat-send-btn"
                  aria-label="Send message"
                  style={{
                    width: '38px', height: '38px', flexShrink: 0,
                    borderRadius: '8px',
                    background: BRAND.forest,
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.15s',
                  }}
                >
                  <Send style={{ width: '16px', height: '16px' }} />
                </button>
              </form>

              {/* Brand footer */}
              <div style={{
                padding: '6px 14px 8px',
                background: BRAND.surface,
                textAlign: 'center',
                borderTop: `1px solid ${BRAND.forestDark}08`,
              }}>
                <span style={{
                  fontSize: '10px', color: BRAND.sage,
                  fontFamily: 'monospace', letterSpacing: '0.1em', textTransform: 'uppercase',
                }}>
                  Powered by Mahi AI
                </span>
              </div>
          </>
        </div>
      )}
    </>
  );
};

export default LiveChat;